/**
 * Copy for "Apply to saved blocks" on the Employees / Equipment tabs — the
 * preview dialog's lines and the toast after the write. Pure, so every
 * sentence the PM reads about money is pinned by a test rather than eyeballed.
 */
import type {
  ApplyRatesToSavedKind,
  ApplyRatesToSavedResponse,
  ApplyRatesWeekRef,
} from '@coffman-tm/shared';
import { formatDateMd, formatMoneyCents, formatSubmissionLabel } from './format';

/** Above this many (PCO, week)s the dialog gives the count, not the list. */
export const APPLY_RATES_WEEK_LIST_MAX = 8;

export function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

function signedMoney(cents: number): string {
  return `${cents > 0 ? '+' : ''}${formatMoneyCents(cents)}`;
}

/**
 * "PCO 004.2, week ending 9/12" — the billing package's own name, so a split
 * week's two open packages read as two different lines. A week with no rollup
 * yet has no package, and reads as the PCO alone.
 */
function weekName(w: ApplyRatesWeekRef): string {
  return `${formatSubmissionLabel(w.pco_number, w.submission_number)}, week ending ${formatDateMd(w.week_ending_date)}`;
}

/** React key for a week line: one per rollup, or per (PCO, week) when there is none yet. */
export function applyRatesWeekKey(w: ApplyRatesWeekRef): string {
  return w.rollup_id ?? `${w.pco_id}|${w.week_ending_date}`;
}

export function applyRatesCountsLine(p: ApplyRatesToSavedResponse): string {
  return `${plural(p.rows, 'row')} across ${plural(p.blocks, 'block')} in ${plural(p.weeks, 'week')}.`;
}

/** The delta is lines only: markup (and, for equipment, the tax) ride on top. */
export function applyRatesDeltaLine(kind: ApplyRatesToSavedKind, cents: number): string {
  const before = kind === 'equipment' ? 'before markup and equipment tax' : 'before markup';
  return `Billing on open weeks changes by ${signedMoney(cents)} ${before}.`;
}

export function applyRatesWeekLine(w: ApplyRatesToSavedResponse['week_deltas'][number]): string {
  return `${weekName(w)}: ${signedMoney(w.delta_cents)}`;
}

export function applyRatesSentBeforeLine(n: number): string | null {
  if (n === 0) return null;
  return n === 1
    ? '1 of these weeks was sent before and is reopened; it will be re-priced too.'
    : `${n} of these weeks were sent before and are reopened; they will be re-priced too.`;
}

export function applyRatesUnmatchedLine(n: number): string | null {
  if (n === 0) return null;
  return `${plural(n, 'row')} with no roster match will be left alone.`;
}

export function applyRatesNothingText(p: ApplyRatesToSavedResponse): string {
  return p.open_weeks === 0
    ? 'Nothing to re-price: every week with saved work has already been sent.'
    : 'Nothing to re-price: every saved block on an open week already uses the current rates.';
}

export interface ApplyRatesToast {
  variant: 'success' | 'info' | 'warning';
  title?: string;
  body: string;
}

export function applyRatesResultToast(r: ApplyRatesToSavedResponse): ApplyRatesToast {
  const failed = r.failed_weeks ?? [];
  const locked = r.skipped_locked_weeks ?? [];
  const changedMeanwhile = r.skipped_changed_rows ?? 0;
  const clean = failed.length === 0 && locked.length === 0 && changedMeanwhile === 0;
  if (r.rows === 0 && clean) {
    return { variant: 'info', body: applyRatesNothingText(r) };
  }
  const done = `Re-priced ${plural(r.rows, 'row')} across ${plural(r.weeks, 'week')}.`;
  if (clean) return { variant: 'success', body: done };
  const parts = [done];
  if (changedMeanwhile > 0) {
    parts.push(
      changedMeanwhile === 1
        ? '1 row was changed by someone else meanwhile and was left alone.'
        : `${changedMeanwhile} rows were changed by someone else meanwhile and were left alone.`,
    );
  }
  if (locked.length > 0) {
    const names = locked.map(weekName).join('; ');
    parts.push(
      `${names} ${locked.length === 1 ? 'was' : 'were'} sent while this was open, so ${locked.length === 1 ? 'it was' : 'they were'} left alone.`,
    );
  }
  if (failed.length > 0) {
    const names = failed.map(weekName).join('; ');
    parts.push(
      `The new rates are in place, but the totals for ${names} didn't refresh. Contact support if they look wrong.`,
    );
  }
  return { variant: 'warning', title: 'Rates applied — one thing to check', body: parts.join(' ') };
}
